import { config } from '../config/environment'

export class ApiError extends Error {
  /**
   * `code` / `detail` mirror the API's ApiError record, and are only populated for JSON error
   * bodies. Callers that need to branch on a specific failure (e.g. FOLDER_NOT_EMPTY vs
   * CONFLICT_DUPLICATE, both 409) read `code`; the rest can keep using `message` alone.
   */
  constructor(public status: number, message: string, public code?: string, public detail?: unknown) {
    super(message)
    this.name = 'ApiError'
  }
}

const REFRESH_PATH = '/v1/auth/refresh'

// Endpoints where a 401 means the credentials were wrong, not that the session expired. Refreshing
// would be pointless, and the server's message ("Invalid email or password.") must reach the user.
const CREDENTIAL_PATHS = [REFRESH_PATH, '/v1/auth/login', '/v1/auth/mfa/verify', '/v1/auth/mfa/recover',
  '/v1/auth/oauth/google', '/v1/auth/oauth/microsoft', '/v1/auth/oauth/apple', '/v1/auth/oauth/confirm', '/v1/auth/oauth/link']
const MFA_REQUIRED_CODE = 'mfa_required'
const MFA_REQUIRED_PATH = '/mfa-required'

const SIGNED_IN_AREAS = ['/app', '/audit', '/mfa-required']

// Single in-flight refresh shared across concurrent 401s, so N simultaneously-failing requests
// trigger one POST /auth/refresh, not N.
let refreshPromise: Promise<boolean> | null = null

// The currently-selected workspace (Live/Test), sent as X-Workspace-Id on every request so
// list/create endpoints can scope to it. Service instances are module-level singletons built
// before React renders, so this can't live in AppStateContext -- AppStateContext calls
// setActiveWorkspaceId() to keep it in sync. Seeded from localStorage so the very first
// requests (before AppStateContext's effect runs) are already scoped.
let activeWorkspaceId: string | null = localStorage.getItem('docm:currentWorkspaceId')

export function setActiveWorkspaceId(id: string | null): void {
  activeWorkspaceId = id
}

function tryRefresh(apiBaseUrl: string): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch(`${apiBaseUrl}${REFRESH_PATH}`, { method: 'POST', credentials: 'include' })
      .then(r => r.ok)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

class BaseApiService {
  protected readonly apiBaseUrl: string

  constructor() {
    this.apiBaseUrl = config.apiBaseUrl
  }

  async request<T>(
    method: string,
    path: string,
    body?: unknown,
    isBlob = false,
    silent401 = false,
    signal?: AbortSignal,
    isRetry = false
  ): Promise<T> {
    const isFormData = body instanceof FormData
    const headers: HeadersInit = isFormData
      ? {}
      : { 'Content-Type': 'application/json' }
    if (activeWorkspaceId) {
      (headers as Record<string, string>)['X-Workspace-Id'] = activeWorkspaceId
    }

    let response: Response
    try {
      response = await fetch(`${this.apiBaseUrl}${path}`, {
        method,
        credentials: 'include',
        headers,
        signal,
        body:
          body === undefined
            ? undefined
            : isFormData
              ? (body as FormData)
              : JSON.stringify(body),
      })
    } catch (err) {
      throw new ApiError(0, 'Network error')
    }

    if (response.status === 401 && CREDENTIAL_PATHS.some(p => path.startsWith(p))) {
      let message = 'Unauthorized'
      try {
        const data = await response.json()
        message = data.message || data.title || message
      } catch {}
      throw new ApiError(401, message)
    }

    if (response.status === 401) {
      // Transparently refresh-and-retry once (unless we've already retried) before falling back
      // to the redirect-to-login behavior.
      if (!isRetry) {
        const refreshed = await tryRefresh(this.apiBaseUrl)
        if (refreshed) {
          return this.request<T>(method, path, body, isBlob, silent401, signal, true)
        }
      }

      // Only signed-in areas bounce to /login. Public pages (invite and reset links, e-sign, marketing)
      // must stay put for a signed-out visitor, whatever background call happens to 401.
      if (!silent401 && SIGNED_IN_AREAS.some(p => window.location.pathname.startsWith(p))) {
        window.location.href = '/login'
      }
      throw new ApiError(401, 'Unauthorized')
    }

    if (!response.ok) {
      let message = response.statusText || `HTTP ${response.status}`
      let code: string | undefined
      let detail: unknown
      try {
        const data = await response.json()
        message = data.message || data.title || message
        code = data.code
        detail = data.detail
      } catch {}
      // An admin past the MFA deadline is blocked everywhere except /auth; send them to enroll.
      if (response.status === 403 && code === MFA_REQUIRED_CODE && !window.location.pathname.startsWith(MFA_REQUIRED_PATH)) {
        window.location.href = MFA_REQUIRED_PATH
      }
      throw new ApiError(response.status, message, code, detail)
    }

    if (isBlob) {
      return response.blob() as unknown as T
    }

    const text = await response.text()
    if (!text) return {} as T
    return JSON.parse(text) as T
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path)
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body)
  }

  postSilent<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body, false, true)
  }

  postForm<T>(path: string, formData: FormData): Promise<T> {
    return this.request<T>('POST', path, formData)
  }

  /**
   * Multipart POST that reports upload progress. `fetch` has no equivalent of
   * XMLHttpRequest.upload.onprogress, so a large .docx upload through request() looks frozen until
   * the server responds. Mirrors request()'s auth behavior: credentials, workspace header, and a
   * single refresh-and-retry on 401.
   */
  postFormWithProgress<T>(
    path: string,
    formData: FormData,
    onProgress?: (pct: number) => void,
    signal?: AbortSignal,
    isBlob = false,
    isRetry = false
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', `${this.apiBaseUrl}${path}`)
      xhr.withCredentials = true
      if (activeWorkspaceId) xhr.setRequestHeader('X-Workspace-Id', activeWorkspaceId)
      if (isBlob) xhr.responseType = 'blob'

      if (onProgress) {
        xhr.upload.onprogress = e => {
          if (e.lengthComputable) onProgress((e.loaded / e.total) * 100)
        }
      }

      if (signal) {
        if (signal.aborted) { xhr.abort(); reject(new ApiError(0, 'Aborted')); return }
        signal.addEventListener('abort', () => xhr.abort(), { once: true })
      }

      xhr.onerror = () => reject(new ApiError(0, 'Network error'))
      xhr.onabort = () => reject(new ApiError(0, 'Aborted'))

      xhr.onload = async () => {
        if (xhr.status === 401 && !isRetry) {
          const refreshed = await tryRefresh(this.apiBaseUrl)
          if (refreshed) {
            resolve(await this.postFormWithProgress<T>(path, formData, onProgress, signal, isBlob, true))
            return
          }
          const { pathname } = window.location
          if (!pathname.startsWith('/login') && !pathname.startsWith('/auth-redirect')) {
            window.location.href = '/login'
          }
          reject(new ApiError(401, 'Unauthorized'))
          return
        }

        if (xhr.status < 200 || xhr.status >= 300) {
          let message = xhr.statusText || `HTTP ${xhr.status}`
          try {
            const raw = isBlob ? await (xhr.response as Blob).text() : (xhr.responseText as string)
            const data = JSON.parse(raw)
            message = data.message || data.title || message
          } catch {}
          reject(new ApiError(xhr.status, message))
          return
        }

        if (isBlob) { resolve(xhr.response as T); return }
        const text = xhr.responseText
        resolve((text ? JSON.parse(text) : {}) as T)
      }

      xhr.send(formData)
    })
  }

  postFormBlobWithProgress(
    path: string,
    formData: FormData,
    onProgress?: (pct: number) => void,
    signal?: AbortSignal
  ): Promise<Blob> {
    return this.postFormWithProgress<Blob>(path, formData, onProgress, signal, true)
  }

  postBlob(path: string, body?: unknown, signal?: AbortSignal): Promise<Blob> {
    return this.request<Blob>('POST', path, body, true, false, signal)
  }

  getBlob(path: string): Promise<Blob> {
    return this.request<Blob>('GET', path, undefined, true)
  }

  httpDelete<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path)
  }
}

export default BaseApiService
